import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { SupportTicket, SupportMessage, getMessages, sendMessage, createTicket } from '../api';
import './chat.css';

interface ChatInterfaceProps {
    ticket: SupportTicket | null;
    onTicketCreated: (ticket: SupportTicket) => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ ticket, onTicketCreated }) => {
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState<SupportMessage[]>([]);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (ticket) {
            loadMessages();
        } else {
            setMessages([]);
        }
    }, [ticket?.id]);

    const loadMessages = async () => {
        if (!ticket) return;
        try {
            const data = await getMessages(ticket.id);
            setMessages(data);
        } catch (err) {
            console.error('Failed to load messages:', err);
        }
    };

    const handleSendMessage = async () => {
        if (!message.trim()) return;
        setIsSending(true);
        try {
            if (ticket) {
                await sendMessage(ticket.id, message);
                await loadMessages();
            } else {
                const response = await createTicket(message);
                if (response.ok) {
                    const newTicket = await response.json();
                    onTicketCreated(newTicket);
                } else {
                    throw new Error('Failed to create ticket');
                }
            }
            setMessage('');
        } catch (err) {
            console.error('Failed to send message:', err);
        } finally {
            setIsSending(false);
        }
    };

    const renderMessages = () => {
        let currentDate = '';
        return messages.map((msg: SupportMessage) => {
            const messageDate = format(new Date(msg.createdAt), 'dd.MM.yyyy');
            const showDateSeparator = messageDate !== currentDate;
            currentDate = messageDate;

            return (
                <React.Fragment key={msg.id}>
                    {showDateSeparator && (
                        <div className="chat-date">
                            {messageDate}
                        </div>
                    )}
                    {msg.isStaffMessage && (
                        <div className="operator-info">
                            Slidy Operator {format(new Date(msg.createdAt), 'HH:mm')}
                        </div>
                    )}
                    <Box 
                        className={`message ${msg.isStaffMessage ? 'staff' : 'user'}`}
                    >
                        <Typography variant="caption" className="message-meta">
                            {!msg.isStaffMessage && (
                                <>
                                    <span>You</span>
                                    <span>{format(new Date(msg.createdAt), 'HH:mm')}</span>
                                </>
                            )}
                        </Typography>
                        <Typography>{msg.content}</Typography>
                    </Box>
                </React.Fragment>
            );
        });
    };

    return (
        <Box className="chat-interface">
            <Box className="chat-header">
                <Typography variant="h6">
                    {ticket ? `Ticket #${ticket.id}` : 'New Support Request'}
                </Typography>
                <Typography variant="caption">{format(new Date(), 'dd.MM.yyyy')}</Typography>
            </Box>

            <Box className="chat-messages">
                {ticket ? (
                    renderMessages()
                ) : (
                    <div className="chat-placeholder">
                        <p>Start a new conversation with our support team.</p>
                        <p>Type your message below and we'll get back to you as soon as possible.</p>
                    </div>
                )}
            </Box>

            <Box className="chat-input">
                <TextField
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={ticket ? "Write a message..." : "Describe your issue..."}
                    disabled={isSending}
                    multiline
                    rows={2}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={isSending || !message.trim()}
                >
                    <SendIcon />
                </Button>
            </Box>
        </Box>
    );
}; 